import React from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, AlertTriangle, CheckCircle2, TrendingUp, TrendingDown,
  Minus, XCircle, Flag
} from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';

export default function AgentInsights({ insights }) {
  if (!insights || !insights.comparison_summary) return null;

  const stats = insights.sku_changes || [];
  const improved = stats.filter(c => c.change === 'improved' || c.change === 'resolved');
  const recurring = stats.filter(c => c.change === 'no_improvement');
  const worsened = stats.filter(c => c.change === 'worsened');
  const newIssues = stats.filter(c => c.change === 'new_issue');

  const resolutionRate = insights.resolution_rate || 0;
  const escalationRecommended = insights.escalation_recommended;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-5"
      data-testid="agent-insights"
    >
      {/* Main Alert */}
      <Alert className={`border-l-4 ${
        escalationRecommended
          ? 'border-l-red-500 bg-red-50/50'
          : resolutionRate >= 0.7
          ? 'border-l-emerald-500 bg-emerald-50/50'
          : 'border-l-amber-500 bg-amber-50/50'
      }`}>
        <div className="flex items-start gap-2.5">
          <Sparkles className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
            escalationRecommended
              ? 'text-red-600'
              : resolutionRate >= 0.7
              ? 'text-emerald-600'
              : 'text-amber-600'
          }`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-semibold text-foreground">
                Agent Insights
              </p>
              {escalationRecommended && (
                <Badge variant="destructive" className="h-4 text-[10px]">
                  <Flag className="w-2.5 h-2.5 mr-1" />
                  Escalation Recommended
                </Badge>
              )}
            </div>
            <AlertDescription className="text-xs space-y-1.5">
              <p className="font-medium">{insights.comparison_summary}</p>
              {insights.key_findings && insights.key_findings.length > 0 && (
                <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                  {insights.key_findings.map((finding, idx) => (
                    <li key={idx}>{finding}</li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </div>
        </div>
      </Alert>

      {/* Detailed Breakdown (expandable) */}
      {stats.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {/* Improved */}
          {improved.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
              <div className="flex items-center gap-1 text-emerald-700 mb-1">
                <CheckCircle2 className="w-3 h-3" />
                <span className="text-xs font-semibold">Improved</span>
              </div>
              <p className="text-xl font-bold text-emerald-900 tabular-nums">
                {improved.length}
              </p>
              <p className="text-[10px] text-emerald-700/70 mt-0.5">
                {improved.length === 1 ? 'SKU fixed' : 'SKUs fixed'}
              </p>
            </div>
          )}

          {/* Recurring */}
          {recurring.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <div className="flex items-center gap-1 text-amber-700 mb-1">
                <Minus className="w-3 h-3" />
                <span className="text-xs font-semibold">Recurring</span>
              </div>
              <p className="text-xl font-bold text-amber-900 tabular-nums">
                {recurring.length}
              </p>
              <p className="text-[10px] text-amber-700/70 mt-0.5">
                Still REJECTED
              </p>
            </div>
          )}

          {/* Worsened */}
          {worsened.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
              <div className="flex items-center gap-1 text-red-700 mb-1">
                <TrendingDown className="w-3 h-3" />
                <span className="text-xs font-semibold">Worsened</span>
              </div>
              <p className="text-xl font-bold text-red-900 tabular-nums">
                {worsened.length}
              </p>
              <p className="text-[10px] text-red-700/70 mt-0.5">
                Deteriorated
              </p>
            </div>
          )}

          {/* New Issues */}
          {newIssues.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5">
              <div className="flex items-center gap-1 text-purple-700 mb-1">
                <XCircle className="w-3 h-3" />
                <span className="text-xs font-semibold">New Issues</span>
              </div>
              <p className="text-xl font-bold text-purple-900 tabular-nums">
                {newIssues.length}
              </p>
              <p className="text-[10px] text-purple-700/70 mt-0.5">
                First time REJECT
              </p>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
